import { useCallback, useEffect, useRef } from 'react';

import { useParams } from 'react-router-dom';

import useInterval from '@/app/hooks/useInterval';
import { createBreadcrumbBuffer } from './breadcrumbBuffer';
import sendLocationBreadcrumbs from './sendLocationBreadcrumbs';

const FLUSH_EVERY_MS = 15 * 1000;
const WATCH_OPTIONS: PositionOptions = { enableHighAccuracy: true, maximumAge: 0 };

/**
 * Records the customer phone's GPS track during a live LVE call and pushes it
 * to the backend in batches, so the recording player can replay the walk on a
 * mini-map (CAD-6600/CAD-6601). Best-effort by design: permission denial or a
 * missing GPS must never surface to the customer.
 */
export default function useLocationBreadcrumbs(enabled: boolean) {
  const { hash } = useParams();
  const bufferRef = useRef(createBreadcrumbBuffer());

  useEffect(() => {
    if (!enabled || !navigator.geolocation) {
      return;
    }
    const watchId = navigator.geolocation.watchPosition(
      position => bufferRef.current.accept(position),
      () => undefined,
      WATCH_OPTIONS,
    );
    return () => navigator.geolocation.clearWatch(watchId);
  }, [enabled]);

  const flush = useCallback((keepalive = false) => {
    if (!hash) {
      return;
    }
    const points = bufferRef.current.drain();
    if (!points.length) {
      return;
    }
    void sendLocationBreadcrumbs(hash, points, { keepalive }).then(sent => {
      if (!sent) {
        bufferRef.current.restore(points);
      }
    });
  }, [hash]);

  useInterval(() => flush(), FLUSH_EVERY_MS);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    const flushOnPageHide = () => flush(true);
    window.addEventListener('pagehide', flushOnPageHide);
    return () => {
      window.removeEventListener('pagehide', flushOnPageHide);
      flush(true);
    };
  }, [enabled, flush]);
}
